import { ApiResult, HttpClient } from "../http.js";

/**
 * GET /api/companies/me/dpa. `currentVersion` is null while no DPA text is shipped: hide every
 * DPA element then. A 404 means the same (older backend). Never show a gate or a popup; the status
 * lives quietly under Indstillinger.
 */
export interface DpaStatusDto {
  currentVersion: string | null;
  accepted: boolean;
  /** The current version while it is not accepted, else null. */
  pendingVersion: string | null;
  /** Whether the caller may accept for the company (settings.editCompany). */
  canAccept: boolean;
  /** yyyy-MM-dd. */
  effectiveFrom: string | null;
  /** The acceptance of the current version, else the most recent earlier one. */
  acceptance: DpaAcceptanceSummaryDto | null;
}

export interface DpaAcceptanceSummaryDto {
  version: string;
  acceptedAt: string;
  acceptedByName: string;
}

/** One block of the shared DPA JSON contract. Fields of the other block types are null. */
export interface DpaBlockDto {
  type: "p" | "h3" | "ul" | "ol" | "table";
  /** `p` and `h3`. */
  text: string | null;
  /** `ul` and `ol`. */
  items: string[] | null;
  /** `table`; the first row is the header. */
  rows: string[][] | null;
}

export interface DpaSectionDto {
  id: string;
  heading: string;
  blocks: DpaBlockDto[];
}

/**
 * GET /api/legal/dpa/current (anonymous). `{{controller.name}}`, `{{controller.cvr}}` and
 * `{{controller.address}}` are left in the text. 404 while no DPA text is shipped.
 */
export interface DpaDocumentDto {
  version: string;
  /** yyyy-MM-dd. */
  publishedAt: string;
  /** yyyy-MM-dd. */
  effectiveFrom: string;
  title: string;
  textSha256: string;
  sections: DpaSectionDto[];
}

/** Body of POST /api/companies/me/dpa/accept. 409 unless `version` is the current one. */
export interface AcceptDpaPayload {
  version: string;
}

/**
 * Optional on every company-creation payload (create, bootstrap, partner-conversion): "the person
 * creating this company accepted this version". The server records who, when, IP and text hash.
 * Omit it when the status says no DPA is published; a request without it is never rejected.
 */
export interface DpaAcceptanceInput {
  version: string;
}

/** Platform staff: one row of the notice import. Identify the company by `companyId` or `cvr`. */
export interface DpaNoticeInput {
  companyId?: string | null;
  cvr?: string | null;
  email: string;
  /** ISO timestamp; defaults to now on the server. */
  notifiedAt?: string | null;
}

export interface RecordDpaNoticesPayload {
  version: string;
  notices: DpaNoticeInput[];
}

export type DpaNoticeOutcomeStatus = "recorded" | "duplicate" | "company_not_found" | "invalid";

export interface RecordDpaNoticesResultDto {
  outcomes: { index: number; status: DpaNoticeOutcomeStatus }[];
}

/** Platform staff: an owner or admin to email about a version. */
export interface DpaRecipientDto {
  companyId: string;
  companyName: string;
  cvr: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  accepted: boolean;
  notifiedAt: string | null;
}

export class DpaApi {
  constructor(private readonly http: HttpClient) {}

  /** Any member. */
  getStatus(): Promise<ApiResult<DpaStatusDto>> {
    return this.http.get<DpaStatusDto>("/api/companies/me/dpa");
  }

  /** Company admin. Idempotent: accepting an already accepted version answers the same status. */
  accept(payload: AcceptDpaPayload): Promise<ApiResult<DpaStatusDto>> {
    return this.http.post<DpaStatusDto>("/api/companies/me/dpa/accept", { body: payload });
  }

  /** Any member. Omit `version` for the current one; an unaccepted version is watermarked. */
  getPdf(version?: string): Promise<ApiResult<ArrayBuffer>> {
    return this.http.getBinary("/api/companies/me/dpa/pdf", version ? { query: { version } } : {});
  }

  /** Anonymous. */
  getCurrentText(): Promise<ApiResult<DpaDocumentDto>> {
    return this.http.get<DpaDocumentDto>("/api/legal/dpa/current");
  }

  /** Anonymous. The current text as a PDF without company data, so an admin can read it before accepting. */
  getCurrentPdf(): Promise<ApiResult<ArrayBuffer>> {
    return this.http.getBinary("/api/legal/dpa/current/pdf");
  }

  /** Platform staff only. */
  listRecipients(options: { version?: string; onlyPending?: boolean } = {}): Promise<ApiResult<DpaRecipientDto[]>> {
    const query: Record<string, string | boolean> = {};
    if (options.version) query.version = options.version;
    if (options.onlyPending) query.onlyPending = true;
    return this.http.get<DpaRecipientDto[]>("/api/admin/dpa/recipients", { query });
  }

  /** Platform staff only. Logs notices; sends nothing. */
  recordNotices(payload: RecordDpaNoticesPayload): Promise<ApiResult<RecordDpaNoticesResultDto>> {
    return this.http.post<RecordDpaNoticesResultDto>("/api/admin/dpa/notices", { body: payload });
  }
}
